import { useCallback, useEffect, useRef, useState } from "react";
import { Fingerprint, ShieldCheck } from "lucide-react";

import { usePostHog } from "@posthog/react";

import { useCreatePasskey } from "../../hooks/useCreatePasskey";
import {
  authMfaEnrolled,
  authMfaSetupShown,
  authPasskeyEnrollDeclined,
  authPasskeyEnrolled,
  captureAuthEvent,
  type AuthPasskeyDeclineReason,
} from "../../lib/auth-analytics";
import type { MfaTrigger } from "../../lib/auth/mfa-gate";
import { AuthenticatorSetupPanel, type Enrollment } from "./AuthenticatorSetupPanel";
import { Button } from "../primitives/Button";

export const SETUP_SECURITY_COPY: Record<MfaTrigger, string> = {
  acquisition:
    "Before you add buyers, protect their data with 2-step verification. It takes about a minute.",
  has_clients:
    "Your buyers' data needs 2-step verification. It takes about a minute.",
};

export interface SetupSecurityScreenProps {
  trigger: MfaTrigger;
  /** Start on this step (default "totp"); used by the DEV preview route. */
  initialStep?: "totp" | "passkey";
  /** DEV preview route only: local fixtures, no Clerk. */
  preview?: boolean;
  /** DEV preview only: open step 1 already showing the QR enrollment. */
  initialEnrollment?: Enrollment;
  /** Both steps are done (or the optional passkey step was skipped). */
  onComplete: () => void;
  /** Secondary "Not now" under the primary action (acquisition trigger only). */
  onNotNow?: () => void;
  // Optional observers; the screen itself emits the allowlisted analytics.
  onShown?: (trigger: MfaTrigger) => void;
  onTotpEnrolled?: () => void;
  onPasskeyEnrolled?: () => void;
  onPasskeySkipped?: () => void;
  onPasskeyDeclined?: (reason: AuthPasskeyDeclineReason) => void;
}

/**
 * Full-screen `/setup-security` interstitial (spec 7.2). Step 1: authenticator
 * app (REQUIRED, no skip). Step 2: optional passkey ("Next time, skip the
 * code.").
 */
export function SetupSecurityScreen({
  trigger,
  initialStep = "totp",
  preview = false,
  initialEnrollment,
  onComplete,
  onNotNow,
  onShown,
  onTotpEnrolled,
  onPasskeyEnrolled,
  onPasskeySkipped,
  onPasskeyDeclined,
}: SetupSecurityScreenProps) {
  const posthog = usePostHog();
  const [step, setStep] = useState<"totp" | "passkey">(initialStep);
  const shown = useRef(false);
  useEffect(() => {
    if (shown.current) return;
    shown.current = true;
    captureAuthEvent(posthog, authMfaSetupShown(trigger));
    onShown?.(trigger);
  }, [onShown, trigger, posthog]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-warm p-6">
      <div className="grid w-full max-w-[560px] gap-5">
        <div className="text-center">
          <ShieldCheck className="mx-auto mb-2 size-8 text-pro" />
          <h1 className="view-h1 text-[22px]">Set up 2-step verification</h1>
          <p className="view-sub mt-2.5">{SETUP_SECURITY_COPY[trigger]}</p>
        </div>
        {step === "totp" ? (
          <div className="grid justify-items-center gap-3">
            <p className="text-center text-sm font-semibold">Step 1 of 2: Authenticator app</p>
            <AuthenticatorSetupPanel
              align="center"
              preview={preview}
              initialEnrollment={initialEnrollment}
              onVerified={() => {
                captureAuthEvent(posthog, authMfaEnrolled("totp"));
                onTotpEnrolled?.();
              }}
              onComplete={() => setStep("passkey")}
            />
          </div>
        ) : (
          <PasskeyStep
            preview={preview}
            onDone={onComplete}
            onEnrolled={() => {
              captureAuthEvent(posthog, authPasskeyEnrolled("pro_setup"));
              captureAuthEvent(posthog, authMfaEnrolled("passkey"));
              onPasskeyEnrolled?.();
            }}
            onSkipped={() => {
              captureAuthEvent(posthog, authPasskeyEnrollDeclined("dismissed"));
              onPasskeySkipped?.();
            }}
            onDeclined={(reason) => {
              captureAuthEvent(posthog, authPasskeyEnrollDeclined(reason));
              onPasskeyDeclined?.(reason);
            }}
          />
        )}
        {onNotNow ? (
          <div className="flex justify-center">
            <button type="button" className="ra-view" onClick={onNotNow}>
              Not now
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const usePreviewPasskey = (): (() => Promise<{ status: "enrolled" }>) =>
  useCallback(async () => ({ status: "enrolled" as const }), []);

function PasskeyStep({
  preview,
  onDone,
  onEnrolled,
  onSkipped,
  onDeclined,
}: {
  preview?: boolean;
  onDone: () => void;
  onEnrolled?: () => void;
  onSkipped?: () => void;
  onDeclined?: (reason: AuthPasskeyDeclineReason) => void;
}) {
  return preview ? (
    <PasskeyStepBody useCreate={usePreviewPasskey} {...{ onDone, onEnrolled, onSkipped, onDeclined }} />
  ) : (
    <PasskeyStepBody useCreate={useCreatePasskey} {...{ onDone, onEnrolled, onSkipped, onDeclined }} />
  );
}

function PasskeyStepBody({
  useCreate,
  onDone,
  onEnrolled,
  onSkipped,
  onDeclined,
}: {
  useCreate: typeof useCreatePasskey;
  onDone: () => void;
  onEnrolled?: () => void;
  onSkipped?: () => void;
  onDeclined?: (reason: AuthPasskeyDeclineReason) => void;
}) {
  const createPasskey = useCreate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const supported = typeof PublicKeyCredential !== "undefined";

  async function add() {
    if (busy) return;
    setBusy(true);
    setError(null);
    const result = await createPasskey();
    setBusy(false);
    if (result.status === "enrolled") {
      onEnrolled?.();
      onDone();
    } else if (result.status === "error") {
      onDeclined?.("error");
      setError(result.message);
    } else {
      onDeclined?.("cancelled");
    }
  }

  return (
    <div className="grid justify-items-center gap-3" data-testid="passkey-step">
      <p className="text-center text-sm font-semibold">
        Step 2 of 2: Passkey <span className="font-normal text-fg-muted">(optional)</span>
      </p>
      <div className="grid justify-items-center gap-2">
        <Fingerprint className="size-5 text-fg-muted" />
        <p className="text-center text-sm text-fg-muted">
          Next time, skip the code. Sign in with your face, fingerprint, or
          device PIN.
        </p>
      </div>
      {error ? (
        <p className="text-center text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
      <div className="flex justify-center gap-2">
        <Button
          variant="outline"
          onClick={() => {
            onSkipped?.();
            onDone();
          }}
        >
          Skip for now
        </Button>
        {supported ? (
          <Button disabled={busy} onClick={() => void add()}>
            {busy ? "Adding…" : "Add a passkey"}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
